import { useCallback, useEffect, useState } from 'react';

/**
 * The measured width of an element (container-based, not viewport: the V1 card and V2 module are
 * narrow on a wide screen). `null` until measured (or while not laid out), and always null where ResizeObserver does not
 * exist. Returns `[width, ref]`; attach the ref to the element to watch.
 */
/** A zero width means "not laid out" (hidden, detached, jsdom): unmeasured, never "narrow". */
const measured = (w: number): number | null => (w > 0 ? w : null);

export function useContainerWidth(): [number | null, (el: HTMLElement | null) => void] {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const ref = useCallback((node: HTMLElement | null) => setEl(node), []);
  useEffect(() => {
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    setWidth(measured(el.getBoundingClientRect().width));
    const observer = new ResizeObserver(entries => {
      const next = entries[0]?.contentRect.width;
      if (typeof next === 'number') setWidth(measured(next));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return [width, ref];
}
